import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useApp } from '@/core/state/appStore'
import { ModalHeader, type TabOverrides } from '@/core/components/modal/ModalHeader'
import { ErrorBoundary } from '@/core/components/ErrorBoundary'
import { ServerForm } from '@/core/components/form/FormRenderer'
import {
  isShellFeldEmpty,
  isShellFeldValueInvalid,
  collectVisibleShellLeafFelder,
  dropHiddenShellWerte,
  shellFelder,
} from '@/core/components/form/formModel'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import { AskModal } from '@/core/components/ui/ask-modal'
import {
  Tooltip,
  Button,
  Callout,
  Icon,
  LoadingState,
  FormScroll,
  Title,
  toastError,
  toastSuccess,
} from 'admination-react'
import { ApiError, assertWriteIdentity, captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import type { Shell, ShellAnmerkungen, ShellFeld, ShellHinweise, ShellQuellen, ShellWert, ShellWerte } from '@/core/templates/types'
import { useAutosave } from '@/core/hooks/useAutosave'

export type ShellFormularDaten = {
  schale: unknown
  werte: Record<string, unknown>
  quellen: Record<string, string>
  hinweise?: ShellHinweise
  anmerkungen?: ShellAnmerkungen
  vermerk?: string | null
}

export type ShellFormularProblem = { feld: string; label: string; grund: string }

export type ShellFormularHinweis = { feld: string; text: string }

export type ShellFormularApi<E = unknown> = {
  laden: (coachingId: string) => Promise<ShellFormularDaten>
  vorschau?: (coachingId: string, werte: ShellWerte) => Promise<Blob>
  speichern: (coachingId: string, werte: ShellWerte, bestaetigt: boolean) => Promise<E>
  entwurfSpeichern?: (coachingId: string, werte: ShellWerte) => Promise<void>
  probleme: (error: unknown) => ShellFormularProblem[] | null
  hinweise?: (error: unknown) => ShellFormularHinweis[] | null
  nachSpeichern?: (coachingId: string, ergebnis: E) => void
}

export type ShellFormularTexte = {
  name: string
  gespeichert: string
  ladenFehlgeschlagen: string
  speichernFehlgeschlagen: string
}

function angabenText(n: number): string {
  return n === 1 ? "1 Angabe fehlt oder passt nicht" : `${n} Angaben fehlen oder passen nicht`
}

type Failure =
  | { kind: 'conflict'; message: string }
  | { kind: 'refused'; probleme: ShellFormularProblem[] }
  | { kind: 'technical' }

function classifyFailure(error: unknown, api: Pick<ShellFormularApi, 'probleme'>, name: string): Failure {
  if (error instanceof ApiError && error.status === 409) {
    if (error.detailText) return { kind: 'conflict', message: error.detailText }
    console.error(`${name}: 409 ohne detail, Antwortkörper:`, error.detail)
    return { kind: 'conflict', message: "Unerwartete Antwort des Servers (409)." }
  }
  const probleme = api.probleme(error)
  if (probleme) return { kind: 'refused', probleme }
  return { kind: 'technical' }
}

function splitProbleme(
  schema: Shell,
  values: ShellWerte,
  quellen: ShellQuellen | undefined,
  probleme: ShellFormularProblem[],
): { shown: { feld: string; grund: string }[]; hidden: { label: string; grund: string }[] } {
  const visibleEditableIds = new Set(
    collectVisibleShellLeafFelder(shellFelder(schema), values, quellen)
      .filter((feld) => !feld.nur_lesen)
      .map((feld) => feld.id),
  )
  const shown: { feld: string; grund: string }[] = []
  const hidden: { label: string; grund: string }[] = []
  for (const p of probleme) {
    if (visibleEditableIds.has(p.feld)) shown.push({ feld: p.feld, grund: p.grund })
    else hidden.push({ label: p.label, grund: p.grund })
  }
  return { shown, hidden }
}

function refusalToastText(totalCount: number, hidden: { label: string; grund: string }[]): string {
  const base = angabenText(totalCount)
  if (hidden.length === 0) return base
  const extra = hidden.map((h) => `${h.label}: ${h.grund}`).join('; ')
  return `${base} - nicht angezeigt: ${extra}`
}

type ShellFormularBoxProps<E> = {
  coachingId: string | null
  api: ShellFormularApi<E>
  texte: ShellFormularTexte
  kopfAktionen?: (ctx: { saving: boolean }) => ReactNode
  attemptedAdvance?: boolean
  tabs?: TabOverrides
  meldetNichtGespeichert?: boolean
  ueberschrift?: boolean
  speichernText?: string
  fuer?: string
  fragen?: boolean
  frozenFields?: ReadonlySet<string>
}

const offenText = (n: number) => (n === 1 ? "Noch 1 Pflichtfeld offen" : `Noch ${n} Pflichtfelder offen`)
const invalidText = (n: number) => (n === 1 ? "1 Angabe ungültig" : `${n} Angaben ungültig`)

const kannUngueltigSein = (felder: readonly ShellFeld[]): boolean =>
  felder.some((feld) => feld.art === 'zahl' || kannUngueltigSein(feld.felder ?? []))

export function ShellFormularBox<E>(props: ShellFormularBoxProps<E>) {
  const { dispatch } = useApp()
  return (
    <ErrorBoundary
      key={props.coachingId}
      fallback={(
        <>
          <ModalHeader tabs={props.tabs} onClose={() => dispatch({ type: 'CLOSE' })} />
          <main className="form">
            <p>{props.texte.ladenFehlgeschlagen}</p>
          </main>
        </>
      )}
      onError={() => toastError(props.texte.ladenFehlgeschlagen)}
    >
      <ShellFormular {...props} />
    </ErrorBoundary>
  )
}

function ShellFormular<E>({
  coachingId,
  api,
  texte,
  kopfAktionen,
  attemptedAdvance = true,
  tabs,
  meldetNichtGespeichert = false,
  ueberschrift = false,
  speichernText,
  fuer,
  fragen = false,
  frozenFields,
}: ShellFormularBoxProps<E>) {
  const workspaceIdentity = useRef(captureWriteIdentity()).current
  const { dispatch, reloadDashboard } = useApp()
  const [data, setData] = useState<ShellFormularDaten | null>(null)
  const [ladefehler, setLadefehler] = useState(false)
  const [values, setValues] = useState<ShellWerte>({})
  const [view, setView] = useState<'form' | 'pdf'>('form')
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverErrors, setServerErrors] = useState<Map<string, string> | undefined>(undefined)
  const [renderCrashed, setRenderCrashed] = useState(false)
  const [hinweise, setHinweise] = useState<ShellFormularHinweis[] | null>(null)
  const [nichtGespeichert, setNichtGespeichert] = useState(false)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const savingRef = useRef(false)

  const weiterRef = useRef(false)

  const valuesRef = useRef<ShellWerte>({})

  const gehalteneWelche = useRef<ShellWert | undefined>(undefined)

  const meldeLadefehler = useCallback(
    (e: unknown) => {
      const failure = classifyFailure(e, api, texte.name)
      toastError(failure.kind === 'conflict' ? failure.message : texte.ladenFehlgeschlagen)
    },
    [api, texte],
  )

  useEffect(() => {
    let cancelled = false
    setData(null)
    setLadefehler(false)
    setValues({})
    valuesRef.current = {}
    gehalteneWelche.current = undefined
    setView('form')
    setPdfBlob(null)
    setServerErrors(undefined)
    setRenderCrashed(false)
    setHinweise(null)
    setNichtGespeichert(false)
    if (!coachingId) return
    api
      .laden(coachingId)
      .then((result) => {
        if (cancelled || !isWriteIdentityCurrent(workspaceIdentity)) return
        const werte = dropHiddenShellWerte(
          result.schale as Shell,
          result.werte as ShellWerte,
          result.quellen as ShellQuellen,
        )
        setData(result)
        setValues(werte)
        valuesRef.current = werte
      })
      .catch((e) => {
        console.error(`${texte.name} laden fehlgeschlagen:`, e)
        if (!cancelled && isWriteIdentityCurrent(workspaceIdentity)) {
          setLadefehler(true)
          meldeLadefehler(e)
        }
      })
    return () => {
      cancelled = true
    }
  }, [coachingId, api, texte, meldeLadefehler, workspaceIdentity])

  const saveDraft = useCallback(
    (werte: ShellWerte): Promise<void> | undefined => {
      if (!coachingId || !api.entwurfSpeichern) return undefined
      assertWriteIdentity(workspaceIdentity)
      return api
        .entwurfSpeichern(coachingId, werte)
        .catch((e) => {
          console.error(`${texte.name} Entwurf speichern fehlgeschlagen:`, e)
          throw e
        })
    },
    [coachingId, api, texte, workspaceIdentity],
  )
  const { flush: flushDraft, status: draftStatus, error: draftError } = useAutosave(values, data !== null && !!coachingId && !!api.entwurfSpeichern && !saving, saveDraft, 250, { form: texte.name, record: coachingId ?? undefined, label: texte.name, recover: (restored) => { valuesRef.current = restored; setValues(restored) } })

  useEffect(() => {
    if (draftStatus !== 'error' || !draftError) return
    const probleme = api.probleme(draftError)
    if (probleme) setServerErrors(new Map(probleme.map((problem) => [problem.feld, problem.grund])))
  }, [api, draftStatus, draftError])

  function handleChange(id: string, value: unknown) {
    if (savingRef.current) return
    const next: ShellWerte = { ...valuesRef.current, [id]: value as ShellWerte[string] }
    valuesRef.current = next
    setValues(next)
    setServerErrors((prev) => {
      if (!prev?.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }

  function requestClose() {
    dispatch({ type: 'CLOSE' })
  }

  function showRefusal(probleme: ShellFormularProblem[]) {
    const { shown, hidden } = schema
      ? splitProbleme(schema, values, quellen, probleme)
      : { shown: probleme.map((p) => ({ feld: p.feld, grund: p.grund })), hidden: [] }
    toastError(refusalToastText(probleme.length, hidden))
    return shown
  }

  async function weiter() {
    if (!coachingId || !api.vorschau || weiterRef.current || savingRef.current) return
    weiterRef.current = true
    setPdfLoading(true)
    setView('pdf')
    try {
      await flushDraft()
      assertWriteIdentity(workspaceIdentity)
      const blob = await api.vorschau(coachingId, valuesRef.current)
      if (!alive.current || !isWriteIdentityCurrent(workspaceIdentity)) return
      setPdfBlob(blob)
    } catch (e) {
      console.error(`${texte.name} Vorschau fehlgeschlagen:`, e)
      if (!alive.current || !isWriteIdentityCurrent(workspaceIdentity)) return
      setView('form')
      const failure = classifyFailure(e, api, texte.name)
      if (failure.kind === 'refused') {
        const shown = showRefusal(failure.probleme)
        setServerErrors(new Map(shown.map((p) => [p.feld, p.grund])))
      } else {
        toastError("Die Vorschau konnte nicht erstellt werden.")
      }
    } finally {
      if (alive.current) setPdfLoading(false)
      weiterRef.current = false
    }
  }

  async function speichern(bestaetigt: boolean) {
    if (!coachingId) return
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    try {
      await flushDraft()
      assertWriteIdentity(workspaceIdentity)
      const ergebnis = await api.speichern(coachingId, valuesRef.current, bestaetigt)
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      toastSuccess(texte.gespeichert)
      if (alive.current) setNichtGespeichert(false)
      if (alive.current) {
        if (api.nachSpeichern) api.nachSpeichern(coachingId, ergebnis)
        else dispatch({ type: 'CLOSE' })
      }
      reloadDashboard()
    } catch (e) {
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      console.error(`${texte.name} speichern fehlgeschlagen:`, e)
      const warnung = api.hinweise?.(e) ?? null
      if (warnung) {
        if (alive.current) setHinweise(warnung)
        return
      }
      const failure = classifyFailure(e, api, texte.name)
      if (failure.kind !== 'conflict' && meldetNichtGespeichert && alive.current) setNichtGespeichert(true)
      if (failure.kind === 'conflict') {
        toastError(failure.message)
        if (alive.current) dispatch({ type: 'CLOSE' })
        reloadDashboard()
      } else if (failure.kind === 'refused') {
        const shown = showRefusal(failure.probleme)
        if (alive.current) {
          setServerErrors(new Map(shown.map((p) => [p.feld, p.grund])))
          setView('form')
        }
      } else {
        toastError(texte.speichernFehlgeschlagen)
      }
    } finally {
      savingRef.current = false
      if (alive.current) setSaving(false)
    }
  }

  const schema = data ? (data.schale as Shell) : null
  const quellen = data ? (data.quellen as ShellQuellen) : undefined
  const felder = schema ? collectVisibleShellLeafFelder(shellFelder(schema), values, quellen) : []
  const offen =
    felder.filter((feld) => feld.pflicht === true && isShellFeldEmpty(feld, values[feld.id])).length +
    felder.filter((feld) => data?.hinweise?.[feld.id] !== undefined).length
  const invalid = felder.filter((feld) => isShellFeldValueInvalid(feld, values[feld.id])).length
  const weiterHint = [offen > 0 ? offenText(offen) : null, invalid > 0 ? invalidText(invalid) : null]
    .filter((part): part is string => part !== null)
    .join(' · ')
  const statusReserve = schema
    ? [offenText(99), ...(kannUngueltigSein(shellFelder(schema)) ? [invalidText(99)] : [])].join(' · ')
    : ''

  const showFormActions = view === 'form' || pdfLoading
  const finalizeAction = (
    <div key="finalize" className="pill">
      <Button
        primaryAction
        iconOnly={!speichernText}
        title={speichernText ? undefined : "Dokument abschließen"}
        loading={saving}
        disabled={showFormActions ? !data || offen > 0 || invalid > 0 || saving || (!!speichernText && pdfLoading) : undefined}
        onClick={() => void speichern(false)}
      >
        <Icon name="i-check" />{speichernText}
      </Button>
    </div>
  )

  return (
    <>
      <ModalHeader size={view === 'pdf' ? 'document' : 'form'} tabs={tabs} onClose={requestClose} closeDisabled={saving || pdfLoading}>
        <div className="pill-cluster">

          {kopfAktionen?.({ saving })}
          {!renderCrashed &&
            (showFormActions ? (
              <>
                <div key="hints" className="modal-form-hints">
                  {schema && (
                    <Tooltip><span className="field-hint is-info" data-reserve={statusReserve} title={weiterHint}>
                      {weiterHint}
                    </span></Tooltip>
                  )}
                  <span className="field-hint is-error">{nichtGespeichert && !saving ? "Nicht gespeichert" : ''}</span>
                </div>
                {speichernText && finalizeAction}
                {api.vorschau ? (
                  <div key="preview" className="pill">
                    <Button primaryAction={!speichernText} iconOnly title="Weiter" loading={pdfLoading} disabled={!data || offen > 0 || invalid > 0 || saving} onClick={() => void weiter()}>
                      <Icon name="i-doc" />
                    </Button>
                  </div>
                ) : !speichernText && finalizeAction}
              </>
            ) : (
              <>
                {speichernText && finalizeAction}
                <div key="back" className="pill">
                  <Button iconOnly title="Zurück" disabled={saving} onClick={() => setView('form')}>
                    <Icon name="i-edit" />
                  </Button>
                </div>
                {!speichernText && finalizeAction}
              </>
            ))}
        </div>
      </ModalHeader>
      {view === 'form' ? (
        <FormScroll>
          {!data && coachingId && (ladefehler ? (
            <Callout tone="error" role="alert">{texte.ladenFehlgeschlagen}</Callout>
          ) : (
            <LoadingState>{texte.name}{" wird geladen …"}</LoadingState>
          ))}
          {fuer && (
            <p>
              <strong>{fuer}</strong>
            </p>
          )}
          {schema && ueberschrift && (
            <div className="section">
              <div className="section-body">
                <Title as="h2">{schema.titel}</Title>
                <p>{[schema.nummer, schema.untertitel].filter(Boolean).join(' · ')}</p>
              </div>
            </div>
          )}
          {schema && !ueberschrift && (schema.nummer || schema.untertitel) && (
            <p>{[schema.nummer, schema.untertitel].filter(Boolean).join(' · ')}</p>
          )}
          {data?.vermerk && <p>{data.vermerk}</p>}
          {schema && (
            <ErrorBoundary
              fallback={<p>Das Formular konnte nicht geladen werden.</p>}
              onError={() => {
                toastError("Das Formular konnte nicht geladen werden.")
                setRenderCrashed(true)
              }}
            >
              <fieldset disabled={saving} className="form-contents"><ServerForm
                template={schema}
                values={values}
                onChange={handleChange}
                quellen={quellen}
                vorbelegung={data?.werte as ShellWerte | undefined}
                gehalteneWelche={gehalteneWelche}
                hinweise={data?.hinweise}
                anmerkungen={data?.anmerkungen}
                serverErrors={serverErrors}
                attemptedAdvance={attemptedAdvance}
                fragen={fragen}
                frozenFields={frozenFields}
              /></fieldset>
            </ErrorBoundary>
          )}
        </FormScroll>
      ) : (
        <PdfRegion
          notice={Object.entries(data?.anmerkungen ?? {}).map(([feld, anmerkung]) => (
            <Fragment key={feld}>{anmerkung}</Fragment>
          ))}
          path={null}
          localBlob={pdfBlob}
          waiting={pdfLoading}
          loadingLabel="Die Vorschau wird erstellt …"
          placeholder="Die Vorschau konnte nicht erstellt werden."
        />
      )}
      <AskModal
        open={hinweise !== null}
        icon="i-doc-check"
        iconStroke
        variant="is-warning"
        question="Angaben weichen vom VerBIS-Lebenslauf ab"
        description={hinweise?.map((h, i) => (
          <span key={h.feld}>
            {i > 0 && <br />}
            {h.text}
          </span>
        ))}
        confirmLabel="Trotzdem speichern"
        cancelLabel="Zurück"
        onConfirm={() => {
          setHinweise(null)
          void speichern(true)
        }}
        onCancel={() => setHinweise(null)}
      />
    </>
  )
}
